import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// ===========================================
// COMPREHENSIVE SEO KEYWORDS DATABASE
// Optimized for: Trek, Circuits 4x4, Retraites Spirituelles
// ===========================================

const seoKeywords = {
  // ===============================
  // FRENCH KEYWORDS - PRIORITAIRES
  // ===============================
  french: {
    trek: [
      // Mots-clés principaux
      "trek désert Maroc", "trek dans le désert marocain", "trekking désert Maroc",
      "randonnée désert Maroc", "randonnée dans le Sahara marocain", "trek Sahara Maroc",
      "trek Sahara marocain", "trek nomade Maroc", "randonnée saharienne", "trek désert Sahara",
      // Expressions plus précises
      "trek désert Maroc 3 jours", "trek désert Maroc 4 jours", "trek désert Maroc 5 jours",
      "trek désert Maroc 7 jours", "trek dans le Sahara avec dromadaires",
      "trek nomade dans le désert marocain", "trek désert au départ de Marrakech",
      "trek désert au départ de Ouarzazate", "trek désert M'Hamid", "trek Erg Chegaga",
      "trek Erg Zahar", "trek désert hors des sentiers battus", "trek désert Maroc en petit groupe",
      "trek privé désert Maroc", "marche dans le désert Maroc", "randonnée pédestre Sahara",
      "méharée Maroc", "randonnée chameau désert", "trek dunes Maroc"
    ],
    circuits4x4: [
      // Mots-clés principaux
      "circuit 4x4 Maroc", "circuit 4x4 désert Maroc", "circuit 4x4 Sahara Maroc",
      "raid 4x4 Maroc", "raid 4x4 désert Maroc", "excursion 4x4 désert Maroc",
      "safari 4x4 désert Maroc", "voyage 4x4 Sahara Maroc", "circuit Sahara en 4x4",
      "circuit désert marocain en 4x4",
      // Expressions longue traîne
      "circuit 4x4 désert au départ de Marrakech", "circuit 4x4 Sahara au départ de Marrakech",
      "circuit 4x4 M'Hamid", "circuit 4x4 Erg Chegaga", "circuit 4x4 Erg Chegaga depuis Marrakech",
      "circuit 4x4 désert Maroc sur mesure", "circuit 4x4 privé Maroc",
      "circuit 4x4 Maroc en petit groupe", "circuit 4x4 désert avec bivouac",
      "circuit 4x4 désert et bivouac nomade", "excursion 4x4 Ouarzazate", "circuit 4x4 Zagora",
      "tour 4x4 Sahara", "aventure 4x4 désert Maroc"
    ],
    retraites: [
      // Mots-clés principaux
      "retraite spirituelle Maroc", "retraite spirituelle désert Maroc",
      "retraite spirituelle dans le désert", "retraite dans le désert marocain",
      "retraite Sahara Maroc", "retraite bien-être Maroc", "retraite méditation Maroc",
      "retraite yoga Maroc", "retraite yoga désert Maroc", "retraite méditation désert Maroc",
      // Expressions plus spécialisées
      "retraite spirituelle Sahara", "retraite spirituelle dans le Sahara marocain",
      "retraite yoga et méditation désert Maroc", "retraite bien-être dans le désert Maroc",
      "retraite développement personnel Maroc", "retraite reconnexion à soi Maroc",
      "retraite déconnexion désert Maroc", "retraite transformation personnelle Maroc",
      "retraite silence désert Maroc", "retraite méditative désert Maroc",
      "retraite holistique Maroc", "retraite holistique désert Maroc",
      "retraite énergétique Maroc", "retraite breathwork Maroc", "retraite sound healing Maroc",
      "stage yoga désert", "stage méditation Sahara", "séjour ressourcement désert"
    ],
    general: [
      "voyage désert Maroc", "séjour Sahara Maroc", "vacances désert marocain",
      "bivouac désert Sahara", "nuit sous les étoiles désert", "camping désert Maroc",
      "dunes Erg Chegaga", "dunes Erg Chebbi", "dunes Erg Zahar",
      "vallée du Draa", "route des kasbahs", "Ait Ben Haddou",
      "oasis Maroc", "kasbah désert", "palmeraie Zagora", "désert M'hamid",
      "porte du désert", "agence voyage désert Maroc", "tour opérateur Sahara"
    ]
  },

  // ===============================
  // ENGLISH KEYWORDS
  // ===============================
  english: {
    trek: [
      "desert trek Morocco", "Morocco desert trekking", "Sahara desert trek",
      "hiking in Moroccan desert", "Sahara hiking Morocco", "desert walking tour Morocco",
      "camel trek Sahara Morocco", "nomad trek Morocco", "desert trek from Marrakech",
      "Erg Chegaga trek", "M'Hamid desert trek", "3 day desert trek Morocco",
      "4 day desert trek Morocco", "5 day desert trek Morocco", "7 day desert trek Morocco",
      "private desert trek Morocco", "small group desert trek Morocco",
      "off the beaten path desert Morocco", "authentic desert experience Morocco"
    ],
    circuits4x4: [
      "4x4 tour Morocco", "4x4 desert tour Morocco", "4x4 Sahara tour Morocco",
      "jeep safari Morocco desert", "4x4 adventure Morocco", "desert expedition Morocco",
      "4x4 tour from Marrakech", "Sahara 4x4 tour from Marrakech",
      "M'Hamid 4x4 tour", "Erg Chegaga 4x4 tour", "private 4x4 tour Morocco",
      "small group 4x4 Morocco", "4x4 with desert camping", "luxury desert camp Morocco"
    ],
    retraites: [
      "spiritual retreat Morocco", "desert retreat Morocco", "yoga retreat Morocco",
      "meditation retreat Morocco", "wellness retreat Morocco desert",
      "yoga retreat Sahara", "meditation retreat Sahara Morocco",
      "spiritual retreat Sahara", "wellness retreat desert Morocco",
      "personal development retreat Morocco", "mindfulness retreat Morocco",
      "silent retreat Morocco desert", "holistic retreat Morocco",
      "healing retreat Morocco", "breathwork retreat Morocco", "sound healing Morocco"
    ],
    general: [
      "Morocco desert tour", "Sahara desert trip", "desert camping Morocco",
      "overnight desert camp Morocco", "Erg Chegaga dunes", "Draa valley Morocco",
      "Morocco travel agency", "Sahara tour operator", "Morocco adventure travel"
    ]
  },

  // ===============================
  // SPANISH KEYWORDS
  // ===============================
  spanish: {
    trek: [
      "trek desierto Marruecos", "trekking desierto marroquí", "senderismo Sahara Marruecos",
      "caminata desierto Marruecos", "trek Sahara Marruecos", "excursión a pie desierto",
      "trek con dromedarios Marruecos", "trek nómada Marruecos",
      "trek desierto desde Marrakech", "trek Erg Chegaga", "trek M'Hamid",
      "trek desierto 3 días", "trek desierto 5 días", "trek privado desierto Marruecos"
    ],
    circuits4x4: [
      "circuito 4x4 Marruecos", "circuito 4x4 desierto Marruecos", "excursión 4x4 Sahara",
      "safari 4x4 desierto Marruecos", "viaje 4x4 Sahara Marruecos",
      "circuito 4x4 desde Marrakech", "circuito 4x4 Erg Chegaga",
      "tour 4x4 desierto Marruecos", "aventura 4x4 Sahara"
    ],
    retraites: [
      "retiro espiritual Marruecos", "retiro desierto Marruecos", "retiro yoga Marruecos",
      "retiro meditación Marruecos", "retiro bienestar desierto Marruecos",
      "retiro yoga Sahara", "retiro espiritual Sahara", "retiro desarrollo personal Marruecos",
      "retiro silencio desierto", "retiro holístico Marruecos"
    ],
    general: [
      "viaje desierto Marruecos", "excursión Sahara", "vacaciones desierto marroquí",
      "noche en el desierto Marruecos", "dunas Marruecos", "campamento desierto"
    ]
  },

  // ===============================
  // DUTCH KEYWORDS
  // ===============================
  dutch: {
    trek: [
      "woestijn trek Marokko", "trekking woestijn Marokko", "Sahara trek Marokko",
      "wandelen in de woestijn Marokko", "kameel trek Sahara", "nomaden trek Marokko",
      "woestijn trek vanaf Marrakech", "Erg Chegaga trek", "M'Hamid woestijn trek",
      "3 dagen woestijn trek", "5 dagen woestijn trek", "privé woestijn trek Marokko"
    ],
    circuits4x4: [
      "4x4 tour Marokko", "4x4 woestijn tour Marokko", "4x4 Sahara tour",
      "jeep safari Marokko woestijn", "4x4 avontuur Marokko",
      "4x4 tour vanaf Marrakech", "4x4 Erg Chegaga tour", "4x4 met woestijn kamperen"
    ],
    retraites: [
      "spirituele retraite Marokko", "woestijn retraite Marokko", "yoga retraite Marokko",
      "meditatie retraite Marokko", "wellness retraite woestijn Marokko",
      "yoga retraite Sahara", "spirituele retraite Sahara", "holistische retraite Marokko"
    ],
    general: [
      "woestijn reis Marokko", "Sahara reis", "woestijn kamperen Marokko",
      "overnachting woestijn Marokko", "Erg Chegaga duinen", "Marokko avontuur"
    ]
  },

  // ===============================
  // ARABIC KEYWORDS (Transliteration)
  // ===============================
  arabic: {
    trek: [
      "رحلة مشي في الصحراء المغرب", "تريكينغ الصحراء المغربية", "المشي في صحراء المغرب",
      "رحلة الجمال الصحراء", "تريك صحراء مغربية", "مشي الكثبان الرملية"
    ],
    circuits4x4: [
      "جولة 4x4 المغرب", "رحلة 4x4 الصحراء المغرب", "سفاري 4x4 الصحراء",
      "جولة 4x4 من مراكش", "مغامرة 4x4 الصحراء"
    ],
    retraites: [
      "خلوة روحية المغرب", "خلوة الصحراء المغرب", "خلوة يوغا المغرب",
      "خلوة تأمل الصحراء", "خلوة الصحراء الروحية"
    ],
    general: [
      "رحلة الصحراء المغرب", "سفر الصحراء", "التخييم في الصحراء",
      "ليلة تحت النجوم الصحراء", "كثبان إرج شكاكة"
    ]
  }
};

// ===========================================
// PAGE-SPECIFIC META DATA - FULLY OPTIMIZED
// ===========================================

const pageMetaData = {
  '/': {
    title: {
      fr: "Trek Désert Maroc | Circuit 4x4 Sahara | Retraite Spirituelle - XploreSahara",
      en: "Morocco Desert Trek | 4x4 Sahara Tour | Spiritual Retreat - XploreSahara",
      ar: "رحلة الصحراء المغرب | جولة 4x4 الصحراء | خلوة روحية - XploreSahara",
      nl: "Woestijn Trek Marokko | 4x4 Sahara Tour | Spirituele Retraite - XploreSahara",
      es: "Trek Desierto Marruecos | Circuito 4x4 Sahara | Retiro Espiritual - XploreSahara"
    },
    description: {
      fr: "XploreSahara - Sahara Soul Treks : Trek désert Maroc, circuits 4x4 Sahara, retraites spirituelles. Départs Marrakech, Ouarzazate, M'Hamid. Erg Chegaga, Erg Zahar, bivouac nomade. Trek 3, 4, 5, 7 jours. Randonnée authentique avec dromadaires.",
      en: "XploreSahara - Sahara Soul Treks: Morocco desert treks, 4x4 Sahara tours, spiritual retreats. Departures from Marrakech, Ouarzazate, M'Hamid. Erg Chegaga, Erg Zahar, nomad camping. 3, 4, 5, 7 day treks. Authentic camel trekking experience.",
      ar: "XploreSahara - رحلات الصحراء المغربية، جولات 4x4 الصحراء، خلوات روحية. الانطلاق من مراكش، ورزازات. إرج شكاكة، التخييم البدوي.",
      nl: "XploreSahara - Sahara Soul Treks: Marokko woestijn treks, 4x4 Sahara tours, spirituele retraites. Vertrek vanuit Marrakech, Ouarzazate, M'Hamid. Erg Chegaga, nomadisch kamperen.",
      es: "XploreSahara - Sahara Soul Treks: Treks desierto Marruecos, circuitos 4x4 Sahara, retiros espirituales. Salidas desde Marrakech, Ouarzazate, M'Hamid. Erg Chegaga, campamento nómada."
    }
  },
  
  '/circuits': {
    title: {
      fr: "Circuits Désert Maroc - Trek & 4x4 Sahara | Départ Marrakech | XploreSahara",
      en: "Morocco Desert Tours - Trek & 4x4 Sahara | From Marrakech | XploreSahara",
      ar: "جولات الصحراء المغرب - تريك و 4x4 | من مراكش | XploreSahara",
      nl: "Marokko Woestijn Tours - Trek & 4x4 Sahara | Vanaf Marrakech | XploreSahara",
      es: "Circuitos Desierto Marruecos - Trek & 4x4 Sahara | Desde Marrakech | XploreSahara"
    },
    description: {
      fr: "Tous nos circuits désert Maroc : treks à pied, circuits 4x4, méharées. Départ Marrakech, Ouarzazate, M'Hamid. Erg Chegaga, vallée du Draa. De 2 à 13 jours. Bivouac sous les étoiles. Petit groupe ou privé.",
      en: "All our Morocco desert tours: walking treks, 4x4 circuits, camel rides. From Marrakech, Ouarzazate, M'Hamid. Erg Chegaga, Draa valley. 2 to 13 days. Camping under the stars. Small group or private.",
      ar: "جميع جولاتنا في الصحراء المغربية: رحلات المشي، جولات 4x4. من مراكش، ورزازات. إرج شكاكة.",
      nl: "Al onze Marokko woestijn tours: wandeltreks, 4x4 circuits, kameelritten. Vanaf Marrakech, Ouarzazate, M'Hamid.",
      es: "Todos nuestros circuitos desierto Marruecos: treks a pie, circuitos 4x4, paseos en camello. Desde Marrakech, Ouarzazate, M'Hamid."
    }
  },

  '/circuits/4x4': {
    title: {
      fr: "Circuit 4x4 Désert Maroc | Raid 4x4 Sahara Marrakech | Erg Chegaga - XploreSahara",
      en: "4x4 Desert Tour Morocco | Sahara 4x4 Safari from Marrakech | Erg Chegaga - XploreSahara",
      ar: "جولة 4x4 الصحراء المغرب | سفاري 4x4 من مراكش | إرج شكاكة - XploreSahara",
      nl: "4x4 Woestijn Tour Marokko | Sahara 4x4 Safari vanaf Marrakech | Erg Chegaga - XploreSahara",
      es: "Circuito 4x4 Desierto Marruecos | Safari 4x4 Sahara desde Marrakech | Erg Chegaga - XploreSahara"
    },
    description: {
      fr: "Circuits 4x4 désert Maroc au départ de Marrakech, Ouarzazate, Agadir. Raid 4x4 Sahara, safari désert, excursion Erg Chegaga, M'Hamid. Circuit 4x4 sur mesure, privé ou petit groupe. Bivouac nomade, nuit sous les étoiles.",
      en: "4x4 desert tours Morocco from Marrakech, Ouarzazate, Agadir. Sahara 4x4 raid, desert safari, Erg Chegaga excursion, M'Hamid. Custom 4x4 tour, private or small group. Nomad camping, night under the stars.",
      ar: "جولات 4x4 الصحراء المغرب من مراكش، ورزازات، أكادير. سفاري الصحراء، إرج شكاكة، محاميد.",
      nl: "4x4 woestijn tours Marokko vanaf Marrakech, Ouarzazate, Agadir. Sahara 4x4 raid, woestijn safari, Erg Chegaga excursie.",
      es: "Circuitos 4x4 desierto Marruecos desde Marrakech, Ouarzazate, Agadir. Raid 4x4 Sahara, safari desierto, excursión Erg Chegaga."
    }
  },

  '/circuits/trekking': {
    title: {
      fr: "Trek Désert Maroc | Randonnée Sahara Marrakech | Trekking Erg Chegaga - XploreSahara",
      en: "Morocco Desert Trek | Sahara Hiking from Marrakech | Erg Chegaga Trekking - XploreSahara",
      ar: "تريك الصحراء المغرب | المشي في الصحراء من مراكش | تريكينغ إرج شكاكة - XploreSahara",
      nl: "Woestijn Trek Marokko | Sahara Wandelen vanaf Marrakech | Erg Chegaga Trekking - XploreSahara",
      es: "Trek Desierto Marruecos | Senderismo Sahara desde Marrakech | Trekking Erg Chegaga - XploreSahara"
    },
    description: {
      fr: "Trek désert Maroc 3, 4, 5, 7 jours. Randonnée Sahara au départ de Marrakech, Ouarzazate. Trek nomade avec dromadaires, bivouac sous les étoiles. Erg Chegaga, Erg Zahar, M'Hamid. Trek privé ou petit groupe. Randonnée authentique hors des sentiers battus.",
      en: "Morocco desert trek 3, 4, 5, 7 days. Sahara hiking from Marrakech, Ouarzazate. Nomad trek with camels, camping under the stars. Erg Chegaga, Erg Zahar, M'Hamid. Private or small group trek. Authentic off the beaten path hiking.",
      ar: "تريك الصحراء المغرب 3، 4، 5، 7 أيام. المشي في الصحراء من مراكش. تريك مع الجمال، التخييم تحت النجوم.",
      nl: "Marokko woestijn trek 3, 4, 5, 7 dagen. Sahara wandelen vanaf Marrakech. Nomaden trek met kamelen, kamperen onder de sterren.",
      es: "Trek desierto Marruecos 3, 4, 5, 7 días. Senderismo Sahara desde Marrakech. Trek nómada con dromedarios, acampada bajo las estrellas."
    }
  },

  '/stages': {
    title: {
      fr: "Retraite Spirituelle Désert Maroc | Yoga Méditation Sahara | Stage Bien-être - XploreSahara",
      en: "Spiritual Retreat Morocco Desert | Yoga Meditation Sahara | Wellness Retreat - XploreSahara",
      ar: "خلوة روحية الصحراء المغرب | يوغا تأمل الصحراء | خلوة العافية - XploreSahara",
      nl: "Spirituele Retraite Woestijn Marokko | Yoga Meditatie Sahara | Wellness Retraite - XploreSahara",
      es: "Retiro Espiritual Desierto Marruecos | Yoga Meditación Sahara | Retiro Bienestar - XploreSahara"
    },
    description: {
      fr: "Retraites spirituelles dans le désert marocain. Yoga, méditation, bien-être au Sahara. Retraite développement personnel, reconnexion à soi, silence. Stage holistique, breathwork, sound healing. Retraite déconnexion dans le désert. Transformation personnelle au cœur du Sahara.",
      en: "Spiritual retreats in the Moroccan desert. Yoga, meditation, wellness in the Sahara. Personal development retreat, reconnection, silence. Holistic retreat, breathwork, sound healing. Disconnect retreat in the desert. Personal transformation in the heart of the Sahara.",
      ar: "خلوات روحية في الصحراء المغربية. يوغا، تأمل، عافية في الصحراء. خلوة التنمية الشخصية، إعادة الاتصال بالذات.",
      nl: "Spirituele retraites in de Marokkaanse woestijn. Yoga, meditatie, wellness in de Sahara. Persoonlijke ontwikkeling retraite, herontdekking, stilte.",
      es: "Retiros espirituales en el desierto marroquí. Yoga, meditación, bienestar en el Sahara. Retiro desarrollo personal, reconexión, silencio."
    }
  },

  '/departures': {
    title: {
      fr: "Prochains Départs Trek & 4x4 Désert Maroc | Dates Disponibles - XploreSahara",
      en: "Upcoming Desert Trek & 4x4 Departures Morocco | Available Dates - XploreSahara",
      ar: "مواعيد المغادرة القادمة للصحراء المغرب | التواريخ المتاحة - XploreSahara",
      nl: "Komende Vertrekdata Woestijn Trek & 4x4 Marokko - XploreSahara",
      es: "Próximas Salidas Trek & 4x4 Desierto Marruecos | Fechas Disponibles - XploreSahara"
    },
    description: {
      fr: "Calendrier des prochains départs pour nos treks et circuits 4x4 dans le désert marocain. Réservez votre place. Trek désert Maroc, circuit 4x4 Sahara. Départs garantis en petit groupe.",
      en: "Calendar of upcoming departures for our treks and 4x4 tours in the Moroccan desert. Book your spot. Morocco desert trek, Sahara 4x4 tour. Guaranteed small group departures.",
      ar: "تقويم مواعيد المغادرة القادمة لرحلاتنا في الصحراء المغربية. احجز مكانك.",
      nl: "Kalender van komende vertrekdata voor onze treks en 4x4 tours in de Marokkaanse woestijn. Boek uw plek.",
      es: "Calendario de próximas salidas para nuestros treks y circuitos 4x4 en el desierto marroquí. Reserve su plaza."
    }
  },

  '/gallery': {
    title: {
      fr: "Photos Désert Sahara Maroc | Dunes Erg Chegaga | Bivouac - XploreSahara",
      en: "Sahara Desert Photos Morocco | Erg Chegaga Dunes | Desert Camp - XploreSahara",
      ar: "صور الصحراء المغرب | كثبان إرج شكاكة | التخييم - XploreSahara",
      nl: "Sahara Woestijn Foto's Marokko | Erg Chegaga Duinen | Woestijn Kamp - XploreSahara",
      es: "Fotos Desierto Sahara Marruecos | Dunas Erg Chegaga | Campamento - XploreSahara"
    },
    description: {
      fr: "Galerie photos du Sahara marocain. Dunes Erg Chegaga, Erg Zahar, bivouacs nomades, couchers de soleil, caravanes de dromadaires, vallée du Draa, kasbahs. Découvrez la beauté du désert.",
      en: "Photo gallery of the Moroccan Sahara. Erg Chegaga dunes, Erg Zahar, nomad camps, sunsets, camel caravans, Draa valley, kasbahs. Discover the beauty of the desert.",
      ar: "معرض صور الصحراء المغربية. كثبان إرج شكاكة، مخيمات البدو، غروب الشمس، قوافل الجمال.",
      nl: "Fotogalerij van de Marokkaanse Sahara. Erg Chegaga duinen, nomadenkampen, zonsondergangen, kameelkaravanen.",
      es: "Galería fotos del Sahara marroquí. Dunas Erg Chegaga, campamentos nómadas, atardeceres, caravanas de camellos."
    }
  },

  '/blog': {
    title: {
      fr: "Blog Voyage Désert Maroc | Conseils Trek Sahara | Guide Pratique - XploreSahara",
      en: "Morocco Desert Travel Blog | Sahara Trek Tips | Practical Guide - XploreSahara",
      ar: "مدونة السفر الصحراء المغرب | نصائح تريك الصحراء - XploreSahara",
      nl: "Marokko Woestijn Reisblog | Sahara Trek Tips | Praktische Gids - XploreSahara",
      es: "Blog Viaje Desierto Marruecos | Consejos Trek Sahara | Guía Práctica - XploreSahara"
    },
    description: {
      fr: "Blog voyage désert Maroc. Conseils pratiques trek Sahara, quand partir, que voir, préparation voyage, culture berbère, vie nomade. Articles sur Erg Chegaga, M'Hamid, vallée du Draa.",
      en: "Morocco desert travel blog. Practical Sahara trek tips, when to go, what to see, trip preparation, Berber culture, nomad life. Articles on Erg Chegaga, M'Hamid, Draa valley.",
      ar: "مدونة السفر في الصحراء المغربية. نصائح عملية، متى تسافر، ماذا ترى، الثقافة الأمازيغية.",
      nl: "Marokko woestijn reisblog. Praktische Sahara trek tips, wanneer te gaan, wat te zien, reisvoorbereiding.",
      es: "Blog viaje desierto Marruecos. Consejos prácticos trek Sahara, cuándo ir, qué ver, preparación viaje, cultura bereber."
    }
  },

  '/contact': {
    title: {
      fr: "Contact & Réservation Trek Désert Maroc | Devis Gratuit - XploreSahara",
      en: "Contact & Book Desert Trek Morocco | Free Quote - XploreSahara",
      ar: "اتصل واحجز تريك الصحراء المغرب | عرض سعر مجاني - XploreSahara",
      nl: "Contact & Boek Woestijn Trek Marokko | Gratis Offerte - XploreSahara",
      es: "Contacto & Reserva Trek Desierto Marruecos | Presupuesto Gratis - XploreSahara"
    },
    description: {
      fr: "Contactez XploreSahara pour réserver votre trek désert Maroc, circuit 4x4 ou retraite spirituelle. Devis gratuit et personnalisé. Réponse rapide. Conseils d'experts locaux.",
      en: "Contact XploreSahara to book your Morocco desert trek, 4x4 tour or spiritual retreat. Free personalized quote. Quick response. Local expert advice.",
      ar: "اتصل بـ XploreSahara لحجز تريك الصحراء أو جولة 4x4 أو خلوة روحية. عرض سعر مجاني.",
      nl: "Neem contact op met XploreSahara om uw Marokko woestijn trek, 4x4 tour of spirituele retraite te boeken.",
      es: "Contacta con XploreSahara para reservar tu trek desierto Marruecos, circuito 4x4 o retiro espiritual."
    }
  },

  '/experiences': {
    title: {
      fr: "Avis Clients Trek Désert Maroc | Témoignages Voyage Sahara - XploreSahara",
      en: "Customer Reviews Desert Trek Morocco | Sahara Trip Testimonials - XploreSahara",
      ar: "آراء العملاء تريك الصحراء المغرب | شهادات السفر - XploreSahara",
      nl: "Klantbeoordelingen Woestijn Trek Marokko | Sahara Reis Getuigenissen - XploreSahara",
      es: "Opiniones Clientes Trek Desierto Marruecos | Testimonios Viaje Sahara - XploreSahara"
    },
    description: {
      fr: "Découvrez les avis et témoignages de nos voyageurs. Expériences authentiques de trek désert Maroc, circuits 4x4 Sahara, retraites spirituelles. Ce qu'ils disent de leur aventure.",
      en: "Discover reviews and testimonials from our travelers. Authentic experiences of Morocco desert treks, Sahara 4x4 tours, spiritual retreats. What they say about their adventure.",
      ar: "اكتشف آراء وشهادات مسافرينا. تجارب أصيلة لرحلات الصحراء المغربية.",
      nl: "Ontdek beoordelingen en getuigenissen van onze reizigers. Authentieke ervaringen van Marokko woestijn treks.",
      es: "Descubre opiniones y testimonios de nuestros viajeros. Experiencias auténticas de treks desierto Marruecos."
    }
  },

  '/team': {
    title: {
      fr: "Notre Équipe | Guides Locaux Désert Sahara Maroc - XploreSahara",
      en: "Our Team | Local Sahara Desert Guides Morocco - XploreSahara",
      ar: "فريقنا | مرشدون محليون صحراء المغرب - XploreSahara",
      nl: "Ons Team | Lokale Sahara Woestijn Gidsen Marokko - XploreSahara",
      es: "Nuestro Equipo | Guías Locales Desierto Sahara Marruecos - XploreSahara"
    },
    description: {
      fr: "Rencontrez l'équipe XploreSahara. Guides locaux berbères, experts du Sahara depuis des générations. Passionnés du désert marocain, M'Hamid, Erg Chegaga.",
      en: "Meet the XploreSahara team. Local Berber guides, Sahara experts for generations. Passionate about the Moroccan desert, M'Hamid, Erg Chegaga.",
      ar: "تعرف على فريق XploreSahara. مرشدون أمازيغ محليون، خبراء الصحراء منذ أجيال.",
      nl: "Ontmoet het XploreSahara team. Lokale Berber gidsen, Sahara experts sinds generaties.",
      es: "Conoce al equipo XploreSahara. Guías bereberes locales, expertos del Sahara desde generaciones."
    }
  },

  '/cgv': {
    title: {
      fr: "Conditions Générales de Vente | Réservation Voyage Désert - XploreSahara",
      en: "Terms and Conditions | Desert Trip Booking - XploreSahara",
      ar: "الشروط والأحكام | حجز رحلة الصحراء - XploreSahara",
      nl: "Algemene Voorwaarden | Woestijn Reis Boeken - XploreSahara",
      es: "Términos y Condiciones | Reserva Viaje Desierto - XploreSahara"
    },
    description: {
      fr: "Conditions générales de vente XploreSahara. Modalités de réservation, paiement, annulation pour vos treks et circuits dans le désert marocain.",
      en: "XploreSahara terms and conditions. Booking, payment, cancellation policies for your treks and tours in the Moroccan desert.",
      ar: "الشروط والأحكام العامة لـ XploreSahara. سياسات الحجز والدفع والإلغاء.",
      nl: "XploreSahara algemene voorwaarden. Boeking, betaling, annuleringsbeleid voor uw treks en tours.",
      es: "Términos y condiciones XploreSahara. Políticas de reserva, pago, cancelación para sus treks y circuitos."
    }
  }
};

// ===========================================
// DEPARTURE CITY SPECIFIC META
// ===========================================
const departureCityMeta = {
  'marrakech': {
    fr: {
      title: "Trek & Circuit 4x4 Départ Marrakech | Désert Sahara - XploreSahara",
      description: "Treks et circuits 4x4 au départ de Marrakech vers le désert du Sahara. Erg Chegaga, Erg Zahar, M'Hamid, vallée du Draa. De 2 à 7 jours. Bivouac nomade."
    },
    en: {
      title: "Trek & 4x4 Tour from Marrakech | Sahara Desert - XploreSahara",
      description: "Treks and 4x4 tours from Marrakech to the Sahara desert. Erg Chegaga, Erg Zahar, M'Hamid, Draa valley. 2 to 7 days. Nomad camping."
    }
  },
  'ouarzazate': {
    fr: {
      title: "Trek & Circuit 4x4 Départ Ouarzazate | Désert Sahara - XploreSahara",
      description: "Treks et circuits 4x4 au départ de Ouarzazate. Kasbahs, vallée du Draa, Erg Chegaga, M'Hamid. Circuits courts et longs."
    },
    en: {
      title: "Trek & 4x4 Tour from Ouarzazate | Sahara Desert - XploreSahara",
      description: "Treks and 4x4 tours from Ouarzazate. Kasbahs, Draa valley, Erg Chegaga, M'Hamid. Short and long tours."
    }
  },
  'mhamid': {
    fr: {
      title: "Trek & Circuit 4x4 M'Hamid | Porte du Désert Sahara - XploreSahara",
      description: "Treks et circuits 4x4 depuis M'Hamid El Ghizlane, porte du désert. Accès direct Erg Chegaga, Erg Zahar. Trek nomade authentique."
    },
    en: {
      title: "Trek & 4x4 Tour M'Hamid | Gateway to Sahara Desert - XploreSahara",
      description: "Treks and 4x4 tours from M'Hamid El Ghizlane, gateway to the desert. Direct access to Erg Chegaga, Erg Zahar. Authentic nomad trek."
    }
  },
  'zagora': {
    fr: {
      title: "Trek & Circuit 4x4 Départ Zagora | Désert Sahara - XploreSahara",
      description: "Treks et circuits 4x4 au départ de Zagora. Vallée du Draa, palmeraies, désert du Sahara. Excursions et séjours."
    },
    en: {
      title: "Trek & 4x4 Tour from Zagora | Sahara Desert - XploreSahara",
      description: "Treks and 4x4 tours from Zagora. Draa valley, palm groves, Sahara desert. Day trips and stays."
    }
  }
};

// ===========================================
// MAIN SEO HEAD COMPONENT
// ===========================================

const SEOHead = ({ customTitle, customDescription, customKeywords, canonicalPath, imageUrl, structuredData }) => {
  const location = useLocation();
  const { i18n } = useTranslation();
  const currentLang = i18n.language || 'fr';
  
  useEffect(() => {
    const path = canonicalPath || location.pathname;
    // Remove language prefix to get clean path
    const cleanPathWithoutLang = path.replace(/^\/(fr|en|ar|nl|es)/, '') || '/';
    
    // Get page-specific meta
    const pageMeta = pageMetaData[cleanPathWithoutLang] || pageMetaData['/'];
    
    // Get title
    const title = customTitle || pageMeta?.title?.[currentLang] || pageMeta?.title?.fr || 'XploreSahara - Sahara Soul Treks';
    
    // Get description
    const description = customDescription || pageMeta?.description?.[currentLang] || pageMeta?.description?.fr || '';
    
    // Build comprehensive keywords based on language
    const langKeywords = currentLang === 'fr' ? seoKeywords.french :
                         currentLang === 'en' ? seoKeywords.english :
                         currentLang === 'es' ? seoKeywords.spanish :
                         currentLang === 'nl' ? seoKeywords.dutch :
                         currentLang === 'ar' ? seoKeywords.arabic :
                         seoKeywords.french;
    
    let allKeywords = [...langKeywords.general];
    
    // Add page-specific keywords
    if (cleanPathWithoutLang.includes('4x4') || cleanPathWithoutLang.includes('circuit')) {
      allKeywords = [...allKeywords, ...langKeywords.circuits4x4];
    }
    if (cleanPathWithoutLang.includes('trek') || cleanPathWithoutLang.includes('trekking')) {
      allKeywords = [...allKeywords, ...langKeywords.trek];
    }
    if (cleanPathWithoutLang.includes('stage') || cleanPathWithoutLang.includes('retraite') || cleanPathWithoutLang.includes('retreat')) {
      allKeywords = [...allKeywords, ...langKeywords.retraites];
    }
    // Homepage gets all keywords
    if (cleanPathWithoutLang === '/') {
      allKeywords = [...allKeywords, ...langKeywords.trek, ...langKeywords.circuits4x4, ...langKeywords.retraites];
    }
    
    // Add custom keywords
    if (customKeywords) {
      allKeywords = [...allKeywords, ...customKeywords];
    }
    
    const keywordsString = allKeywords.join(', ');
    
    // Update document title
    document.title = title;
    
    // Update or create meta tags
    const updateMetaTag = (name, content, isProperty = false) => {
      const attr = isProperty ? 'property' : 'name';
      let meta = document.querySelector(`meta[${attr}="${name}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attr, name);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };
    
    // Basic SEO meta tags
    updateMetaTag('description', description);
    updateMetaTag('keywords', keywordsString);
    updateMetaTag('author', 'XploreSahara - Sahara Soul Treks - SaharaTrek');
    updateMetaTag('robots', 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1');
    
    // Language and region
    updateMetaTag('language', currentLang);
    updateMetaTag('geo.region', 'MA');
    updateMetaTag('geo.placename', 'Sahara, M\'Hamid El Ghizlane, Maroc');
    updateMetaTag('geo.position', '29.8286;-5.7197');
    updateMetaTag('ICBM', '29.8286, -5.7197');
    
    // Open Graph tags
    const ogImage = imageUrl || 'https://images.unsplash.com/photo-1509023464722-18d996393ca8?w=1200&h=630&fit=crop';
    updateMetaTag('og:title', title, true);
    updateMetaTag('og:description', description, true);
    updateMetaTag('og:type', 'website', true);
    updateMetaTag('og:url', `https://saharatrek.org/${currentLang}${cleanPathWithoutLang}`, true);
    updateMetaTag('og:image', ogImage, true);
    updateMetaTag('og:image:width', '1200', true);
    updateMetaTag('og:image:height', '630', true);
    updateMetaTag('og:site_name', 'XploreSahara - Sahara Soul Treks', true);
    updateMetaTag('og:locale', currentLang === 'fr' ? 'fr_FR' : currentLang === 'ar' ? 'ar_MA' : currentLang === 'es' ? 'es_ES' : currentLang === 'nl' ? 'nl_NL' : 'en_US', true);
    
    // Twitter Card tags
    updateMetaTag('twitter:card', 'summary_large_image');
    updateMetaTag('twitter:title', title);
    updateMetaTag('twitter:description', description);
    updateMetaTag('twitter:image', ogImage);
    updateMetaTag('twitter:site', '@xploresahara');
    
    // Canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', `https://saharatrek.org/${currentLang}${cleanPathWithoutLang}`);
    
    // Alternate language links
    const langs = ['fr', 'en', 'ar', 'nl', 'es'];
    langs.forEach(lang => {
      let alternate = document.querySelector(`link[rel="alternate"][hreflang="${lang}"]`);
      if (!alternate) {
        alternate = document.createElement('link');
        alternate.setAttribute('rel', 'alternate');
        alternate.setAttribute('hreflang', lang);
        document.head.appendChild(alternate);
      }
      alternate.setAttribute('href', `https://saharatrek.org/${lang}${cleanPathWithoutLang}`);
    });
    
    // x-default for language selection
    let xDefault = document.querySelector('link[rel="alternate"][hreflang="x-default"]');
    if (!xDefault) {
      xDefault = document.createElement('link');
      xDefault.setAttribute('rel', 'alternate');
      xDefault.setAttribute('hreflang', 'x-default');
      document.head.appendChild(xDefault);
    }
    xDefault.setAttribute('href', `https://saharatrek.org/fr${cleanPathWithoutLang}`);
    
    // Structured data (JSON-LD)
    if (structuredData) {
      const existingScript = document.querySelector('script[data-seo-structured]');
      if (existingScript) {
        existingScript.remove();
      }
      
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-seo-structured', 'true');
      script.textContent = JSON.stringify(structuredData);
      document.head.appendChild(script);
    }
    
  }, [location.pathname, currentLang, customTitle, customDescription, customKeywords, canonicalPath, imageUrl, structuredData]);
  
  return null;
};

// ===========================================
// SCHEMA.ORG STRUCTURED DATA
// ===========================================

export const generateCircuitSchema = (circuit, lang = 'fr') => {
  return {
    "@context": "https://schema.org",
    "@type": "TouristTrip",
    "name": circuit.title?.[lang] || circuit.title?.fr || circuit.title,
    "description": circuit.description?.[lang] || circuit.description?.fr || circuit.description,
    "touristType": circuit.type === "4x4" ? "Adventure tourist" : "Hiking tourist",
    "itinerary": {
      "@type": "ItemList",
      "numberOfItems": circuit.duration_days,
      "itemListElement": circuit.itinerary?.map((day, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "name": day.title?.[lang] || day.title?.fr || day.title
      })) || []
    },
    "offers": {
      "@type": "Offer",
      "price": circuit.price,
      "priceCurrency": "EUR",
      "availability": "https://schema.org/InStock"
    },
    "provider": organizationSchema
  };
};

export const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "TravelAgency",
  "name": "XploreSahara",
  "alternateName": ["SaharaTrek", "Sahara Soul Treks"],
  "url": "https://saharatrek.org",
  "logo": "https://saharatrek.org/logo.png",
  "description": "Trek désert Maroc, circuits 4x4 Sahara, retraites spirituelles. Agence spécialisée dans les voyages authentiques au Sahara marocain depuis M'Hamid El Ghizlane.",
  "slogan": "Votre aventure authentique dans le Sahara marocain",
  "telephone": "+212 678 22 94 73",
  "email": "contact@saharatrek.org",
  "address": {
    "@type": "PostalAddress",
    "streetAddress": "Douar Zaouia, M'hamid El Ghizlane",
    "addressLocality": "M'hamid El Ghizlane",
    "addressRegion": "Zagora",
    "postalCode": "47402",
    "addressCountry": "MA"
  },
  "geo": {
    "@type": "GeoCoordinates",
    "latitude": 29.8286,
    "longitude": -5.7197
  },
  "openingHours": "Mo-Su 08:00-20:00",
  "priceRange": "€€",
  "areaServed": {
    "@type": "Place",
    "name": "Sahara Desert, Morocco"
  },
  "sameAs": [
    "https://www.facebook.com/xploresahara",
    "https://www.instagram.com/xploresahara",
    "https://www.tripadvisor.com/xploresahara"
  ],
  "hasOfferCatalog": {
    "@type": "OfferCatalog",
    "name": "Trek Désert Maroc & Circuits 4x4 Sahara",
    "itemListElement": [
      {
        "@type": "OfferCatalog",
        "name": "Trek Désert Maroc",
        "description": "Trek Sahara Maroc, randonnée désert, trekking Erg Chegaga, M'Hamid, avec dromadaires"
      },
      {
        "@type": "OfferCatalog",
        "name": "Circuit 4x4 Sahara",
        "description": "Circuit 4x4 désert Maroc, raid 4x4 Sahara, safari désert au départ de Marrakech"
      },
      {
        "@type": "OfferCatalog",
        "name": "Retraites Spirituelles",
        "description": "Retraite yoga désert Maroc, méditation Sahara, bien-être, développement personnel"
      }
    ]
  },
  "knowsAbout": [
    "Trek désert Maroc", "Circuit 4x4 Sahara", "Randonnée Sahara", 
    "Retraite spirituelle désert", "Yoga désert Maroc", "Bivouac nomade",
    "Erg Chegaga", "M'Hamid", "Vallée du Draa"
  ]
};

// FAQ Schema for pages
export const generateFAQSchema = (faqs) => {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };
};

// Export keywords for use in other components
export { seoKeywords, pageMetaData, departureCityMeta };

export default SEOHead;
